import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from '../ui/sonner';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { LockKeyhole, Unlock, Printer, Pencil, Download } from 'lucide-react';
import { DayEditorSheet } from './DayEditorSheet';
import {
  STATE_STYLES, SUB_STATUS_STYLES, SHIFT_META, dayState,
  fmtShortDate, fmtDateTime, groupIntoWeeks, WEEKDAY_LABELS,
} from './constants';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const SHIFT_CODE = Object.fromEntries(SHIFT_META.map((s) => [s.key, s.code]));

export const SubmissionReview = ({ open, onOpenChange, periodId, memberId, onChanged }) => {
  const [data, setData] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [adminNotes, setAdminNotes] = useState('');
  const [editorDate, setEditorDate] = useState(null);
  const [editorOpen, setEditorOpen] = useState(false);

  const fetchData = useCallback(async () => {
    if (!periodId || !memberId) return;
    try {
      const res = await axios.get(`${API_URL}/api/availability/periods/${periodId}/submissions/${memberId}`);
      setData(res.data);
      setAdminNotes(res.data.admin_notes || '');
    } catch (err) {
      toast.error('Failed to load submission');
    }
  }, [periodId, memberId]);

  useEffect(() => { if (open) { setEditMode(false); fetchData(); } }, [open, fetchData]);

  const adminSave = async (days, notes, msg) => {
    try {
      const res = await axios.put(`${API_URL}/api/availability/periods/${periodId}/submissions/${memberId}`, {
        days, general_notes: data?.general_notes || '', admin_notes: notes,
      });
      setData((prev) => ({ ...prev, ...res.data }));
      toast.success(msg);
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    }
  };

  const handleDaySave = (date, entry) => {
    const days = { ...(data?.days || {}) };
    if (entry === null) delete days[date]; else days[date] = entry;
    adminSave(days, adminNotes, 'Submission updated (audited)');
  };

  const handleAction = async (action) => {
    try {
      await axios.post(`${API_URL}/api/availability/periods/${periodId}/submissions/${memberId}/${action}`);
      toast.success(action === 'reopen' ? 'Submission reopened' : 'Submission locked');
      fetchData();
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Action failed');
    }
  };

  const handleExportCSV = () => {
    if (!data) return;
    const esc = (v) => { const s = String(v ?? ''); return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s; };
    const lines = [['Date', 'State', 'Day', 'Swing', 'Evening', 'Overnight', 'All-Day Unavailable', 'Restriction', 'Note'].join(',')];
    (data.dates || []).forEach((d) => {
      const e = (data.days || {})[d];
      const sh = e?.shifts || {};
      lines.push([d, dayState(e), sh.day || '', sh.swing || '', sh.evening || '', sh.overnight || '',
        e?.unavailable_all_day ? 'Yes' : '', e?.restriction || '', e?.note || ''].map(esc).join(','));
    });
    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `availability_${data.member?.name?.replace(/\s+/g, '_')}_${data.period?.name?.replace(/\s+/g, '_')}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const handlePrint = () => {
    if (!data) return;
    const esc = (s) => String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const weeks = groupIntoWeeks(data.dates || []);
    const cellHtml = (d) => {
      if (!d) return '<td style="background:#fafafa"></td>';
      const e = (data.days || {})[d];
      const st = dayState(e);
      const s = STATE_STYLES[st];
      const codes = e && !e.unavailable_all_day
        ? Object.entries(e.shifts || {}).filter(([, v]) => v !== 'unavailable').map(([k, v]) => `${SHIFT_CODE[k]}${v === 'preferred' ? '★' : ''}`).join(' ')
        : (e?.unavailable_all_day ? 'All day off' : '—');
      return `<td style="background:${s.bg};color:${s.text};border:1px solid ${s.border}">
        <div style="font-weight:700;font-size:10px">${new Date(d + 'T00:00:00').getDate()}</div>
        <div style="font-size:9px">${esc(codes)}</div>
        ${e?.restriction ? `<div style="font-size:8px;font-style:italic">${esc(e.restriction)}</div>` : ''}
      </td>`;
    };
    const tableRows = weeks.map((w) => `<tr>${w.map(cellHtml).join('')}</tr>`).join('');
    const statusMeta = SUB_STATUS_STYLES[data.status] || SUB_STATUS_STYLES.not_started;
    const html = `<!doctype html><html><head><title>Availability — ${esc(data.member?.name)}</title><style>
      body{font-family:Arial,sans-serif;margin:24px;color:#1a1a2e}
      h1{font-size:18px;margin:0}
      .sub{font-size:12px;color:#555;margin:2px 0 16px}
      table{width:100%;border-collapse:collapse;table-layout:fixed}
      th{background:#1a1a2e;color:#fff;font-size:10px;padding:4px;text-transform:uppercase}
      td{padding:4px;vertical-align:top;height:44px}
      .meta{display:flex;gap:24px;font-size:11px;margin-bottom:12px}
      .meta b{display:block;font-size:12px}
      .notes{margin-top:14px;font-size:11px;border:1px solid #ccc;border-radius:6px;padding:8px}
      @media print{@page{size:landscape;margin:12mm}}
    </style></head><body>
      <h1>Staff Availability — ${esc(data.member?.name)}</h1>
      <div class="sub">${esc(data.member?.position || 'RSS')} · ${esc(data.member?.department || '3.1 Program')} · ${esc(data.period?.name)}</div>
      <div class="meta">
        <div><b>${esc(statusMeta.label)}</b>Status</div>
        <div><b>${data.submitted_at ? esc(fmtDateTime(data.submitted_at)) : '—'}</b>Submitted</div>
        <div><b>${data.stats?.preferred_count ?? 0}</b>Preferred shifts</div>
        <div><b>${data.stats?.available_count ?? 0}</b>Available shifts</div>
        <div><b>${data.stats?.unavailable_days ?? 0}</b>Unavailable days</div>
      </div>
      <table><thead><tr>${WEEKDAY_LABELS.map((l) => `<th>${l}</th>`).join('')}</tr></thead><tbody>${tableRows}</tbody></table>
      <div style="font-size:9px;margin-top:6px">★ = preferred shift</div>
      ${data.general_notes ? `<div class="notes"><b>Employee scheduling notes:</b> ${esc(data.general_notes)}</div>` : ''}
      <script>window.onload=function(){setTimeout(function(){window.print()},250)}</script>
    </body></html>`;
    const w = window.open('', '_blank', 'width=1000,height=800');
    if (!w) { toast.error('Pop-up blocked — allow pop-ups to print'); return; }
    w.document.write(html);
    w.document.close();
  };

  const statusMeta = SUB_STATUS_STYLES[data?.status] || SUB_STATUS_STYLES.not_started;
  const weeks = data ? groupIntoWeeks(data.dates || []) : [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[860px] max-h-[92vh] overflow-y-auto" data-testid="submission-review-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {data?.member?.name}
            <Badge variant={statusMeta.variant} data-testid="review-status-badge">{statusMeta.label}</Badge>
          </DialogTitle>
          <DialogDescription>
            {data?.member?.position} · {data?.member?.staff_type?.toUpperCase()} · {data?.period?.name}
            {data?.submitted_at ? ` · Submitted ${fmtDateTime(data.submitted_at)}` : ''}
          </DialogDescription>
        </DialogHeader>

        {data && (
          <>
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant={editMode ? 'default' : 'outline'} onClick={() => setEditMode(!editMode)} data-testid="review-edit-toggle">
                <Pencil className="h-3.5 w-3.5 mr-1.5" /> {editMode ? 'Editing (click a day)' : 'Edit Submission'}
              </Button>
              <Button size="sm" variant="outline" onClick={() => handleAction('reopen')} data-testid="review-reopen">
                <Unlock className="h-3.5 w-3.5 mr-1.5" /> Reopen
              </Button>
              <Button size="sm" variant="outline" onClick={() => handleAction('lock')} data-testid="review-lock">
                <LockKeyhole className="h-3.5 w-3.5 mr-1.5" /> Lock
              </Button>
              <Button size="sm" variant="outline" onClick={handlePrint} data-testid="review-print">
                <Printer className="h-3.5 w-3.5 mr-1.5" /> Print
              </Button>
              <Button size="sm" variant="outline" onClick={handleExportCSV} data-testid="review-export">
                <Download className="h-3.5 w-3.5 mr-1.5" /> Export CSV
              </Button>
            </div>

            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="rounded-lg border p-2"><div className="text-lg font-bold text-[#1D9E75]">{data.stats?.preferred_count}</div><div className="text-[10px] text-muted-foreground">Preferred</div></div>
              <div className="rounded-lg border p-2"><div className="text-lg font-bold text-[#3B82C4]">{data.stats?.available_count}</div><div className="text-[10px] text-muted-foreground">Available</div></div>
              <div className="rounded-lg border p-2"><div className="text-lg font-bold text-[#D25454]">{data.stats?.unavailable_days}</div><div className="text-[10px] text-muted-foreground">Unavailable days</div></div>
            </div>

            <div className="space-y-2">
              {weeks.map((week, wi) => (
                <div key={wi}>
                  {wi === 0 && (
                    <div className="grid grid-cols-7 gap-1 mb-1">
                      {WEEKDAY_LABELS.map((l) => <div key={l} className="text-[9px] font-semibold text-center text-muted-foreground uppercase">{l}</div>)}
                    </div>
                  )}
                  <div className="grid grid-cols-7 gap-1">
                    {week.map((d, i) => {
                      if (!d) return <div key={i} />;
                      const e = data.days?.[d];
                      const st = dayState(e);
                      const s = STATE_STYLES[st];
                      return (
                        <button
                          key={d} type="button"
                          onClick={() => { if (editMode) { setEditorDate(d); setEditorOpen(true); } }}
                          className={`min-h-[52px] rounded-md border p-1 text-left ${editMode ? 'hover:ring-2 hover:ring-primary cursor-pointer' : 'cursor-default'}`}
                          style={{ background: s.bg, borderColor: s.border }}
                          data-testid={`review-day-${d}`}
                        >
                          <div className="text-[10px] font-bold" style={{ color: s.text }}>{new Date(d + 'T00:00:00').getDate()}</div>
                          <div className="text-[8px] leading-tight" style={{ color: s.text }}>
                            {e?.unavailable_all_day ? 'Off' : Object.entries(e?.shifts || {}).filter(([, v]) => v !== 'unavailable').map(([k, v]) => `${SHIFT_CODE[k]}${v === 'preferred' ? '★' : ''}`).join(' ')}
                          </div>
                          {e?.restriction && <div className="text-[8px] italic truncate" style={{ color: s.text }}>{e.restriction}</div>}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>

            {data.general_notes && (
              <div className="rounded-lg border p-3 text-sm" data-testid="review-employee-notes">
                <div className="text-xs font-semibold text-muted-foreground mb-1">Employee scheduling notes</div>
                {data.general_notes}
              </div>
            )}

            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-muted-foreground">Admin notes (not visible to staff)</label>
              <Textarea value={adminNotes} onChange={(e) => setAdminNotes(e.target.value)} rows={2} maxLength={1000} data-testid="review-admin-notes" />
              <Button size="sm" variant="outline" onClick={() => adminSave(data.days || {}, adminNotes, 'Admin notes saved')} data-testid="review-save-admin-notes">
                Save Admin Notes
              </Button>
            </div>
          </>
        )}

        <DayEditorSheet
          open={editorOpen} onOpenChange={setEditorOpen}
          date={editorDate} entry={editorDate ? data?.days?.[editorDate] : null}
          editable onSave={handleDaySave}
        />
      </DialogContent>
    </Dialog>
  );
};
